import { Button, RamblaActionButtonContent } from 'components';
import ConditionalSpinner from 'components/ConditionalSpinner/ConditionalSpinner';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { toastr } from 'utils';
import { media } from 'utils/rwd';

const ProductCarouselCard = ({ favoriteProduct, isActive, onDelete, isDeleting }) => {
  const { t } = useTranslation('pages');
  const childProduct =
    favoriteProduct.product.child_products.find((cp) => cp.is_featured)?.child_product ??
    favoriteProduct.product.child_products[0]?.child_product;

  if (!childProduct) return null;

  const { picture, parent_product, price, currency, product_code, category, color, size } =
    childProduct;

  const handlePreview = () => {
    if (favoriteProduct.public_url) {
      window.open(favoriteProduct.public_url, '_blank')?.focus();
    }
  };

  return (
    <CardWrapper $isActive={isActive}>
      <ImageWrapper>
        <ProductImage src={picture} alt={parent_product?.name} />
      </ImageWrapper>
      <ProductInfo>
        <ProductName>{parent_product?.name}</ProductName>
        <ProductCode $isEmpty={!product_code}>{product_code || '-'}</ProductCode>
        <AdditionalInfo>
          <ProductMeta $isEmpty={!category?.name}>{category?.name || '-'}</ProductMeta>
          <ColorRow $isEmpty={!color?.name}>
            <ColorDot style={{ backgroundColor: color?.hex_value }} />
            <ProductMeta>{color?.name || '-'}</ProductMeta>
          </ColorRow>
          <SizeRow $isEmpty={!size?.name}>
            <ProductSize>
              <ProductMeta>{size?.name || '-'}</ProductMeta>
            </ProductSize>
          </SizeRow>
        </AdditionalInfo>
        <Footer>
          <ProductPrice $isEmpty={price == null}>
            {price != null ? `${price} ${currency}` : '-'}
          </ProductPrice>
          <FooterButtonsContainer>
            <Button
              variant="primary"
              size="small"
              onClick={() => {
                toastr.confirm(t('toastr:deleteFavoriteProductHeading'), {
                  onOk: () => {
                    onDelete(favoriteProduct.uid);
                  },
                  description: t('toastr:deleteFavoriteProductDescription'),
                  cancelText: t('toastr:cancel'),
                  okText: t('toastr:delete'),
                });
              }}
              disabled={isDeleting}
              style={{ padding: '4px 8px', fontSize: 14 }}
            >
              <ConditionalSpinner showSpinner={isDeleting}>
                {t('retailerPage.products.delete')}
              </ConditionalSpinner>
            </Button>
            {favoriteProduct.public_url && (
              <Button
                variant="avedaPrimary"
                size="small"
                style={{ padding: '4px 8px', fontSize: 14 }}
                onClick={handlePreview}
              >
                <RamblaActionButtonContent icon="preview">
                  {t('retailerPage.products.preview')}
                </RamblaActionButtonContent>
              </Button>
            )}
          </FooterButtonsContainer>
        </Footer>
      </ProductInfo>
    </CardWrapper>
  );
};

export default memo(ProductCarouselCard);

const CardWrapper = styled.div`
  position: relative;
  margin: 0 auto;
  height: 100%;
  max-width: 350px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  border: 2px solid #e9ecef;
  box-shadow: ${({ $isActive }) =>
    $isActive ? '0 4px 10px rgba(0, 0, 0, 0.1)' : '0 2px 10px rgba(0, 0, 0, 0.05)'};
  overflow: hidden;

  ${media.lg`
    margin: 0 8px;
    `}
`;

const ImageWrapper = styled.div`
  width: 100%;
  flex: 0 0 clamp(180px, 42%, 280px);
  min-height: 0;
  overflow: hidden;
  background: #f5f5f5;
  border-bottom: 1px solid #e9ecef;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ProductInfo = styled.div`
  flex: 1;
  padding: 16px;
  display: grid;
  grid-template-rows: auto auto auto minmax(0, 1fr);
  row-gap: 6px;
  min-height: 0;
  overflow-y: auto;
`;

const AdditionalInfo = styled.div`
  display: grid;
  grid-template-rows: auto auto auto;
  gap: 4px;
`;

const ProductName = styled.p`
  margin: 0;
  font-size: 24px;
  line-height: 1.2;
  font-weight: bold;
  color: black;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ProductMeta = styled.span`
  font-size: 16px;
  line-height: 22px;
  color: #6c757d;
  margin: 0;
  visibility: ${({ $isEmpty }) => ($isEmpty ? 'hidden' : 'visible')};
`;

const ProductSize = styled.div`
  display: inline-block;
  align-self: flex-start;
  padding: 4px 8px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 14px;
  color: #6c757d;
`;

const Footer = styled.div`
  align-self: end;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

export const FooterButtonsContainer = styled.div`
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const ProductPrice = styled.span`
  font-size: 24px;
  line-height: 1.2;
  color: #1a2b33;
  font-weight: bold;
  text-decoration: underline 1px #1a2b33;
  text-underline-offset: 4px;
  min-height: 29px;
  text-align: right;
  visibility: ${({ $isEmpty }) => ($isEmpty ? 'hidden' : 'visible')};
`;

const ColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  visibility: ${({ $isEmpty }) => ($isEmpty ? 'hidden' : 'visible')};
`;

const ColorDot = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
`;

const ProductCode = styled(ProductMeta)`
  visibility: ${({ $isEmpty }) => ($isEmpty ? 'hidden' : 'visible')};
`;

const SizeRow = styled.div`
  display: flex;
  visibility: ${({ $isEmpty }) => ($isEmpty ? 'hidden' : 'visible')};
`;
